import { useObserva } from "@morganisbatman/observa-client-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ReadinessVerdict } from "../home/use-readiness";
import { trpc } from "../trpc";

interface MasteryRow {
  topicSlug: string;
  label: string;
  recentAccuracy: number | null;
  attemptCount: number;
}

export interface DiagnosticData {
  pct: number;
  readiness: ReadinessVerdict;
  totalAnswered: number;
  totalCorrect: number;
  weakTopics: MasteryRow[];
}

function computeVerdict(rows: MasteryRow[]): DiagnosticData {
  const seen = rows.filter((t) => t.attemptCount > 0);
  const totalAnswered = seen.reduce((s, t) => s + t.attemptCount, 0);
  const totalCorrect = seen.reduce(
    (s, t) => s + (t.recentAccuracy ?? 0) * t.attemptCount,
    0,
  );
  const pct =
    totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const readiness: ReadinessVerdict =
    pct >= 80 ? "ready" : pct >= 60 ? "almost" : "not-ready";
  const weakTopics = [...seen]
    .sort(
      (a, b) =>
        (a.recentAccuracy ?? 0) - (b.recentAccuracy ?? 0) ||
        b.attemptCount - a.attemptCount,
    )
    .slice(0, 3);
  return {
    pct,
    readiness,
    totalAnswered,
    totalCorrect: Math.round(totalCorrect),
    weakTopics,
  };
}

interface UseDiagnosticReturn {
  data: DiagnosticData | null;
  error: string | null;
  /** Clears the error and re-fetches — wired to the error view's retry. */
  retry: () => void;
}

// Fetches mastery for the resolved vertical, derives the readiness
// verdict + weak topics, and fires the `practice.paywall_viewed`
// observa emit once the data lands. Owns its own fire-once ref so
// re-renders don't double-emit into the checkout funnel.
export function useDiagnostic(
  verticalSlug: string | null,
): UseDiagnosticReturn {
  const observa = useObserva();
  const [mastery, setMastery] = useState<MasteryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const paywallEmittedRef = useRef(false);

  // Bumping `attempt` clears the error and re-runs the fetch effect — the
  // diagnostic is a high-intent pre-paywall step, so a load failure must
  // not dead-end.
  const retry = useCallback(() => {
    setError(null);
    setMastery(null);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!verticalSlug) {
      setError("No credential selected.");
      return;
    }
    void (async () => {
      try {
        const rows = await trpc.mastery.byTopic.query({ verticalSlug });
        setMastery(rows);
      } catch (err) {
        setError(err instanceof Error ? err.message : "failed to load");
      }
    })();
  }, [verticalSlug, attempt]);

  const data = mastery ? computeVerdict(mastery) : null;

  // Fire-once `practice.paywall_viewed` — the first render where data
  // is available is the user "viewing their options." Funnel: this →
  // checkout.started → checkout.succeeded. Emitted from an effect so a
  // discarded render never fires; the ref keeps it once-only.
  useEffect(() => {
    if (verticalSlug && data && !paywallEmittedRef.current) {
      paywallEmittedRef.current = true;
      void observa.event({
        eventType: "practice.paywall_viewed",
        payload: {
          verticalSlug,
          readiness: data.readiness,
          totalAnswered: data.totalAnswered,
          totalCorrect: data.totalCorrect,
        },
      });
    }
  }, [verticalSlug, data]);

  return { data, error, retry };
}
